'use client'

import { LineThumbsUp } from '@qr-front/icons/line-thumbs-up'
import { socialLinkListAtom } from 'features/generator/contentFormSections'
import {
	socialInfoDescriptionAtom,
	socialInfoHeadlineAtom,
} from 'features/generator/contentFormSections/SocialInformationForm'
import { openOverlayIdAtom } from 'features/generator/generatorState'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { QrScreenOverlay } from 'features/generator/QrScreen'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { ReactElement, useMemo } from 'react'

import { TSvgIcon } from '@/types'

import { QrScreen, TQrScreenProps } from '../../../QrScreen/QrScreen'
import { ImageGallery } from '../../Images/ImagesScreen/ImageGallery'
import { ImagesPreview } from '../../Images/ImagesScreen/ImagesPreview'
import { SocialMediaHeader } from './SocialMediaHeader'
import { SocialItem } from './SocialMediaScreenSections/SocialItem'
import { SocialSection } from './SocialMediaScreenSections/SocialSection'
import { socialMediaPrimaryColorAtom } from './SocialMediaScreenState'

const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const t = useTranslations()
	const setOpenOverlayId = useSetAtom(openOverlayIdAtom)
	const headline = useAtomValue(socialInfoHeadlineAtom)
	const description = useAtomValue(socialInfoDescriptionAtom)

	const hasSocialLinks = useAtomValue(socialLinkListAtom).length || demoMode
	const displayedHeadline = demoMode
		? t('generator__qr_content_screen__social_media_type__sections__headline')
		: headline
	const displayedDescription = demoMode
		? t(
				'generator__qr_content_screen__social_media_type__sections__description',
			)
		: description

	const socialSection = useMemo(
		() =>
			hasSocialLinks
				? {
						Icon: LineThumbsUp,
						content: (
							<SocialSection demoMode={demoMode} previewMode={previewMode} />
						),
					}
				: null,
		[demoMode, hasSocialLinks, previewMode],
	)

	const sectionList = useMemo(
		() =>
			[socialSection] as const satisfies ({
				Icon: TSvgIcon
				content: ReactElement
			} | null)[],
		[socialSection],
	)

	return (
		<>
			<QrScreenOverlay
				id="image-gallery"
				previewMode={previewMode}
				className="z-[1] bg-generator-bg"
			>
				<ImageGallery hideOverlay={() => setOpenOverlayId(null)} />
			</QrScreenOverlay>
			<SocialMediaHeader />
			<ImagesPreview
				demoMode={demoMode}
				qrType={QR_TYPE.SocialMedia}
				showOverlay={() => {
					setOpenOverlayId('image-gallery')
				}}
			/>
			<div className="px-5 pb-2 pt-6">
				<h1 className="type-heading-4 text-center text-black transition-colors word-break-fix">
					{displayedHeadline}
				</h1>
				<p className="text-center text-[10px] leading-[16px] text-black transition-colors word-break-fix">
					{displayedDescription}
				</p>
			</div>
			{sectionList
				.filter((value) => value !== null)
				.map(({ content, Icon }, index) => (
					<SocialItem key={index} Icon={Icon} content={content} />
				))}
		</>
	)
}

export const SocialMediaScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(socialMediaPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
